import { useMutation } from "@apollo/client/react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Head } from "../../../../../components/core";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../../contexts/NotifyContext";
import { VehicleBasic } from "../../../../../graphql/fleets/vehicles";
import {
  ADD_FUELENTRY,
  GET_FUELENTRY_BY_VEHICLE_ID,
  VehicleFuelEntryForm,
} from "../../../../../graphql/fleets/vehicles/pages/fuel-entry";
import Form from "../../components/FuelEntryForm";

const FuelEntryCreate = ({
  breadcrumbs,
  vehicleBasic,
}: {
  breadcrumbs: Breadcrumb[];
  vehicleBasic: VehicleBasic | null;
}) => {
  let { vehicleId } = useParams();
  const { t } = useTranslation();
  const { addNotify } = useNotifyContext();
  const navigate = useNavigate();

  const [createVehicleFuelEntry] = useMutation(ADD_FUELENTRY, {
    refetchQueries: [
      { query: GET_FUELENTRY_BY_VEHICLE_ID, variables: { vehicleId } },
    ],
  });
  const handleCreate = (
    values: VehicleFuelEntryForm,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    if (!vehicleId) return;
    createVehicleFuelEntry({
      variables: {
        vehicleId: parseInt(vehicleId),
        vehicleFuelTypeId: values.vehicleFuelTypeId,
        referenceNo: values.referenceNo,
        pricePerUnit: values.pricePerUnit,
        odometer: values.odometer,
        fuelFlags: values.fuelFlags,
        fuelEntryDate: values.fuelEntryDate,
        fuelAmount: values.fuelAmount,
        fleetVendorId: values.fleetVendorId,
        files: values.files,
        comments: values.comments,
      },
    })
      .then(async ({ data }) => {
        if (data?.vehicleFuelEntryCreate) {
          return navigate(
            `/fleets/vehicles/${vehicleId}/fuel-entry/${data.vehicleFuelEntryCreate.vehicleFuelEntry.id}`
          );
        } else {
          toast.error("Something went wrong, please try again later");
        }
        actions.setSubmitting(false);
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "Fuel entry create failed",
          message: error.message,
        });
      });
  };

  return (
    <>
      <Head
        title={FuelEntryCreateResource.name}
        heading={FuelEntryCreateResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: FuelEntryCreateResource.name,
            href: null,
          },
        ]}
      />
      <div className="mx-auto max-w-4xl">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_vehicle_fuel_entry_create")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_vehicle_fuel_entry_create")}
              </p>
            </div>
          </div>

          <Form
            initialValues={{
              vehicleId: Number(vehicleId),
              vehicleFuelTypeId: null,
              referenceNo: "",
              pricePerUnit: null,
              odometer: null,
              fuelFlags: null,
              fuelEntryDate: null,
              fuelAmount: null,
              fleetVendorId: null,
              files: [],
              comments: "",
            }}
            onSubmit={handleCreate}
            submitLabel={t("text_create")}
            onCancel={() => {
              navigate(`/fleets/vehicles/${vehicleId}/fuel-entry`);
            }}
            vehicleBasic={vehicleBasic}
          />
        </div>
      </div>
    </>
  );
};

export default FuelEntryCreate;
export const FuelEntryCreateResource: ResourceProps = {
  name: "Fuel Entry",
  description: "Update an existing vehicle fuel entries",
  access: ["update-vehicles"],
  path: "/fuel-entry/add",
  icon: "bi bi-person",
};
